import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface Props {
  onFilterChange: (filters: Filters) => void;
}

export interface Filters {
  status: string[];
  priority: string[];
  assignee: string[];
  fromDate: string;
  toDate: string;
}

const FilterBar: React.FC<Props> = ({ onFilterChange }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [filters, setFilters] = useState<Filters>({
    status: [],
    priority: [],
    assignee: [],
    fromDate: "",
    toDate: "",
  });

  // Load filters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFilters({
      status: params.getAll("status"),
      priority: params.getAll("priority"),
      assignee: params.getAll("assignee"),
      fromDate: params.get("fromDate") || "",
      toDate: params.get("toDate") || "",
    });
  }, [location.search]);

  // Update URL + notify parent when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    filters.status.forEach((s) => params.append("status", s));
    filters.priority.forEach((p) => params.append("priority", p));
    filters.assignee.forEach((a) => params.append("assignee", a));
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);

    navigate({ search: params.toString() }, { replace: true });
    onFilterChange(filters);
  }, [filters]);

  const toggleFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => {
      const arr = prev[key] as string[];
      return {
        ...prev,
        [key]: arr.includes(value)
          ? arr.filter((v) => v !== value)
          : [...arr, value],
      };
    });
  };

  const clearFilters = () => {
    setFilters({ status: [], priority: [], assignee: [], fromDate: "", toDate: "" });
  };

  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-4 p-2 bg-gray-100 rounded mb-4">
      {/* Status filter */}
      <div className="flex flex-wrap items-center">
        <span className="font-semibold">Status:</span>
        {["To Do", "In Progress", "In Review", "Done"].map((s) => (
          <button
            key={s}
            onClick={() => toggleFilter("status", s)}
            className={`ml-2 mt-1 sm:mt-0 px-2 py-1 rounded text-xs sm:text-sm whitespace-nowrap ${
              filters.status.includes(s) ? "bg-blue-500 text-white" : "bg-gray-200"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Priority filter */}
      <div className="flex flex-wrap items-center">
        <span className="font-semibold">Priority:</span>
        {["Critical", "High", "Medium", "Low"].map((p) => (
          <button
            key={p}
            onClick={() => toggleFilter("priority", p)}
            className={`ml-2 mt-1 sm:mt-0 px-2 py-1 rounded text-xs sm:text-sm whitespace-nowrap ${
              filters.priority.includes(p) ? "bg-blue-500 text-white" : "bg-gray-200"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Assignee filter */}
      <div className="flex flex-wrap items-center">
        <span className="font-semibold">Assignee:</span>
        {["AB", "CD", "EF", "GH", "IJ", "KL"].map((a) => (
          <button
            key={a}
            onClick={() => toggleFilter("assignee", a)}
            className={`ml-2 mt-1 sm:mt-0 px-2 py-1 rounded text-xs sm:text-sm whitespace-nowrap ${
              filters.assignee.includes(a) ? "bg-blue-500 text-white" : "bg-gray-200"
            }`}
          >
            {a}
          </button>
        ))}
      </div>

      {/* Date range */}
      <div className="flex flex-wrap items-center">
        <span className="font-semibold">Due Date:</span>
        <input
          type="date"
          value={filters.fromDate}
          onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
          className="ml-2 mt-1 sm:mt-0 border rounded px-2 py-1 text-xs sm:text-sm w-full sm:w-auto"
        />
        <input
          type="date"
          value={filters.toDate}
          onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
          className="ml-2 mt-1 sm:mt-0 border rounded px-2 py-1 text-xs sm:text-sm w-full sm:w-auto"
        />
      </div>

      {/* Clear filters */}
      {filters.status.length > 0 ||
      filters.priority.length > 0 ||
      filters.assignee.length > 0 ||
      filters.fromDate ||
      filters.toDate ? (
        <button
          onClick={clearFilters}
          className="self-start sm:self-end px-3 py-1 bg-red-500 text-white rounded text-xs sm:text-sm"
        >
          Clear Filters
        </button>
      ) : null}
    </div>
  );
};

export default FilterBar;

